/* Copyright (c) 2025 Xperi. All Rights Reserved. */

/**
 * This module defines the `PreviewComponentAssets` class, which provides various assets and
 * utilities for managing preview components. It includes string and image asset identifiers,
 * placeholder values, and methods for retrieving localized strings and other preview-related
 * data.
 *
 * Key functionalities include:
 * - Providing string and image asset identifiers for localization and UI rendering.
 * - Methods to retrieve localized strings based on preview types and recording actions.
 * - Integration with services to fetch and format data for preview components.
 */

import {
    PreviewType,
    RecordingAvailability,
    RecordingStatus,
    TrickPlay,
} from 'client-code/previews';

import { BrandingUiInfoKeys } from '../entities';
import { ServicesClientImpl } from '../interactors';
import { Locale, LocaleKeys } from '../locale';
import { DateTime } from '../utils';

export class PreviewComponentAssets {
    private mServices: ServicesClientImpl;

    /**
     * Contains image asset identifiers used for referencing images in the application.
     */
    public static ImageAssets = {
        BUTTON_INFO_ICON: 'BUTTON_INFO_ICON',
        CONFLICT_ICON: 'CONFLICT_ICON',
        EMPTY_STAR_ICON: 'EMPTY_STAR_ICON',
        FULL_STAR_ICON: 'FULL_STAR_ICON',
        HALF_STAR_ICON: 'HALF_STAR_ICON',
        HD_ICON: 'HD_ICON',
        HDR_ICON: 'HDR_ICON',
        HYDRA_ICON_DIRECT_PLAY: 'HYDRA_ICON_DIRECT_PLAY',
        HYDRA_ICON_STATUS_DELETED: 'HYDRA_ICON_STATUS_DELETED',
        ICON_STATUS_ONEPASS: 'ICON_STATUS_ONEPASS',
        KUID_ICON: 'KUID_ICON',
        KUID_PARTIAL_ICON: 'KUID_PARTIAL_ICON',
        LOUD_RECORDING_ICON: 'LOUD_RECORDING_ICON',
        NOT_ALLOWED_ICON: 'NOT_ALLOWED_ICON',
        NOT_CURRENTLY_AVAILABLE_ICON: 'NOT_CURRENTLY_AVAILABLE_ICON',
        NOT_RECORDABLE_CELL_ICON: 'NOT_RECORDABLE_CELL_ICON',
        NOT_RECORDABLE_ICON: 'NOT_RECORDABLE_ICON',
        ONEPASS_WISHLIST_ICON: 'ONEPASS_WISHLIST_ICON',
        PPV_ICON: 'PPV_ICON',
        RECORDING_NOW_ICON: 'RECORDING_NOW_ICON',
        RECORDING_NOW_OVER_ART_ICON: 'RECORDING_NOW_OVER_ART_ICON',
        SD_ICON: 'SD_ICON',
        SINGLE_EXPLICIT_RECORD_ICON: 'SINGLE_EXPLICIT_RECORD_ICON',
        SKIP_MODE_ICON: 'SKIP_MODE_ICON',
        SUGGESTION_ICON: 'SUGGESTION_ICON',
        THREE_D_ICON: 'THREE_D_ICON',
        TRANSFER_ICON: 'TRANSFER_ICON',
        TRICKPLAY_RESTRICTED_ICON: 'TRICKPLAY_RESTRICTED_ICON',
        UHD_ICON: 'UHD_ICON',
    };

    /**
     * Contains placeholder identifiers used for string replacements in localization.
     */
    public static Placeholders = {
        AIRDATE: 'AIRDATE',
        DURATION: 'DURATION',
        END_TIME: 'END_TIME',
        HOUR: 'HOUR',
        HOURS: 'HOURS',
        MINUTE: 'MINUTE',
        MINUTES: 'MINUTES',
        PREVIEW_TYPE: 'PREVIEW_TYPE',
        RESTRICTED_TRICKPLAY_MODES: 'RESTRICTED_TRICKPLAY_MODES',
        START_TIME: 'START_TIME',
    };

    /**
     * Contains string asset identifiers used for localization.
     */
    public static StringAssets = {
        DESCRIPTION_NOT_AVAILABLE: LocaleKeys.DESCRIPTION_NOT_AVAILABLE,
        FIRST_AIRED_DATE: LocaleKeys.PS_PREVIEW_FIRST_AIRED_DATE,
        FIRST_AIRS_DATE: LocaleKeys.PS_PREVIEW_FIRST_AIRS_DATE,
        STATUS_MSG_CHANNEL_REC_NOT_ALLOWED: 'RR_CHANNEL_RESTRICTION_MESSAGE',
        STATUS_MSG_COMPLETE_COPYRIGHT_RESTRICTION: 'RR_COPYRIGHT_RESTRICTION_MESSAGE',
        STATUS_MSG_COMPLETE_REC: LocaleKeys.PS_PREVIEW_STATUS_MSG_COMPLETE_REC,
        STATUS_MSG_IN_PROGRESS_REC: LocaleKeys.PS_PREVIEW_STATUS_MSG_IN_PROGRESS_REC,
        STATUS_MSG_PARTIAL_REC: LocaleKeys.PS_PREVIEW_STATUS_MSG_PARTIAL_REC,
        STATUS_MSG_SCHEDULED_AS_ONE_PASS: LocaleKeys.PS_PREVIEW_STATUS_MSG_SCHEDULED_AS_ONE_PASS,
        STATUS_MSG_SCHEDULED_AS_SINGLE_EXPLICIT:
            LocaleKeys.PS_PREVIEW_STATUS_MSG_SCHEDULED_AS_SINGLE_EXPLICIT,
        THUZZ_RATING_DULL: LocaleKeys.PS_PREVIEW_THUZZ_RATING_DULL,
        THUZZ_RATING_GOOD: LocaleKeys.PS_PREVIEW_THUZZ_RATING_GOOD,
        THUZZ_RATING_GREAT: LocaleKeys.PS_PREVIEW_THUZZ_RATING_GREAT,
        THUZZ_RATING_OK: LocaleKeys.PS_PREVIEW_THUZZ_RATING_OK,
        TRICKPLAY_RESTRICTION_ARE_NOT_ALLOWED:
            LocaleKeys.PS_PREVIEW_TRICKPLAY_RESTRICTION_ARE_NOT_ALLOWED,
        TRICKPLAY_RESTRICTION_ARE_NOT_SUPPORTED:
            LocaleKeys.PS_PREVIEW_TRICKPLAY_RESTRICTION_ARE_NOT_SUPPORTED,
        TRICKPLAY_RESTRICTION_IS_NOT_ALLOWED:
            LocaleKeys.PS_PREVIEW_TRICKPLAY_RESTRICTION_IS_NOT_ALLOWED,
        TRICKPLAY_RESTRICTION_IS_NOT_SUPPORTED:
            LocaleKeys.PS_PREVIEW_TRICKPLAY_RESTRICTION_IS_NOT_SUPPORTED,
        TRICKPLAY_RESTRICTION_AND_TEXT: LocaleKeys.TRICKPLAY_AND_TEXT,
        TRICKPLAY_RESTRICTION_FAST_FORWARD: LocaleKeys.TRICKPLAY_FAST_FORWARD,
        TRICKPLAY_RESTRICTION_PAUSE: LocaleKeys.TRICKPLAY_PAUSE,
        TRICKPLAY_RESTRICTION_REWIND: LocaleKeys.TRICKPLAY_REWIND,
        TRICKPLAY_RESTRICTION_UNKNOWN: LocaleKeys.TRICKPLAY_UNKNOWN,
        PREVIEW_LABEL_INCLUDE: LocaleKeys.RIGHT_PREVIEW_ELEMENT_INCLUDE,
        PREVIEW_LABEL_START_FROM: LocaleKeys.RIGHT_PREVIEW_ELEMENT_START_FROM,
        PREVIEW_LABEL_RENT_OR_BUY: LocaleKeys.RIGHT_PREVIEW_ELEMENT_RENT_OR_BUY,
        PREVIEW_LABEL_RECORD_TYPE: LocaleKeys.RIGHT_PREVIEW_ELEMENT_RECORD,
        PREVIEW_LABEL_CHANNEL: LocaleKeys.RIGHT_PREVIEW_ELEMENT_CHANNEL,
        PREVIEW_LABEL_VIDEO_QUALITY: LocaleKeys.RIGHT_PREVIEW_ELEMENT_VIDEO_QUALITY,
        PREVIEW_LABEL_KEEP_AT_MOST: LocaleKeys.RIGHT_PREVIEW_ELEMENT_KEEP_AT_MOST,
        PREVIEW_LABEL_KEEP_UNTIL: LocaleKeys.RIGHT_PREVIEW_ELEMENT_KEEP_UNTIL,
        PREVIEW_LABEL_START_STOP: LocaleKeys.RIGHT_PREVIEW_ELEMENT_START_STOP,
        PREVIEW_KEEP_UNTIL_AS_LONG_AS_POSSIBLE: 'PS_PREVIEW_KEEP_UNTIL_AS_LONG_AS_POSSIBLE',
        PREVIEW_KEEP_UNTIL_SPACE_NEEDED: 'PS_PREVIEW_KEEP_UNTIL_SPACE_NEEDED',
        PREVIEW_LABEL_HOUR_LONGER: 'PS_PREVIEW_LABEL_HOUR_LONGER',
        PREVIEW_LABEL_HOURS_LONGER: 'PS_PREVIEW_LABEL_HOURS_LONGER',
        PREVIEW_LABEL_MINUTE_EARLY: 'PS_PREVIEW_LABEL_MINUTE_EARLY',
        PREVIEW_LABEL_MINUTE_LONGER: 'PS_PREVIEW_LABEL_MINUTE_LONGER',
        PREVIEW_LABEL_MINUTES_EARLY: 'PS_PREVIEW_LABEL_MINUTES_EARLY',
        PREVIEW_LABEL_MINUTES_LONGER: 'PS_PREVIEW_LABEL_MINUTES_LONGER',
        PREVIEW_LABEL_ON_TIME: 'PS_PREVIEW_LABEL_ON_TIME',
        PREVIEW_LABEL_START_REC_EARLY: 'PS_PREVIEW_LABEL_START_REC_EARLY',
        PREVIEW_LABEL_START_REC: 'PS_PREVIEW_LABEL_START_REC',
        PREVIEW_LABEL_STOP_REC_LATE: 'PS_PREVIEW_LABEL_STOP_REC_LATE',
        PREVIEW_LABEL_STOP_REC: 'PS_PREVIEW_LABEL_STOP_REC',
        PREVIEW_LABEL_TIME_AND_CHANNEL: 'PS_PREVIEW_LABEL_TIME_AND_CHANNEL',
    };

    public constructor() {
        this.mServices = new ServicesClientImpl();
    }

    /**
     * Retrieves the content name based on the provided preview type.
     *
     * @param previewType - The type of the preview.
     * @returns A string representing the content name for the given preview type.
     */
    // eslint-disable-next-line class-methods-use-this
    private getContentNameByPreviewType(previewType: string): string {
        switch (previewType) {
            case PreviewType.EpisodePreview:
                return Locale.getString(LocaleKeys.EPISODE);

            case PreviewType.GamePreview:
                return Locale.getString(LocaleKeys.GAME);

            case PreviewType.MatchPreview:
                return Locale.getString(LocaleKeys.MATCH);

            case PreviewType.MoviePreview:
                return Locale.getString(LocaleKeys.MOVIE);

            case PreviewType.ProgramPreview:
                return Locale.getString(LocaleKeys.PROGRAM);

            case PreviewType.RacePreview:
                return Locale.getString(LocaleKeys.RACE);

            case PreviewType.SeriesPreview:
                return Locale.getString(LocaleKeys.SERIES);

            case PreviewType.SportEventPreview:
                return Locale.getString(LocaleKeys.SPORT);

            default:
                return '';
        }
    }

    /**
     * Retrieves the string representation of the first aired date.
     *
     * @param firstAiredDate - The date when the content was first aired.
     * @returns A string representing the first aired date.
     */
    // eslint-disable-next-line class-methods-use-this
    public getFirstArisOrAiredString(firstAiredDate: string): string {
        const oFirstAiredDate = new DateTime(firstAiredDate);
        const isUpcoming: boolean = oFirstAiredDate.getTime() > DateTime.now();
        const displayText = isUpcoming
            ? Locale.getString(PreviewComponentAssets.StringAssets.FIRST_AIRS_DATE)
            : Locale.getString(PreviewComponentAssets.StringAssets.FIRST_AIRED_DATE);

        return displayText.replace(
            PreviewComponentAssets.Placeholders.AIRDATE,
            oFirstAiredDate.strftime('%a %m/%d/%Y'),
        );
    }

    /**
     * Retrieves the URL of an image asset based on the provided asset ID.
     *
     * @param assetId - The identifier of the image asset.
     * @returns A string representing the URL of the image asset.
     */
    public getImageAssetUrlById(assetId: string): string {
        const appImages = this.mServices.getBrandingUiInfoForKey(BrandingUiInfoKeys.APP_IMAGES);
        // eslint-disable-next-line no-restricted-syntax
        for (const imageObj of appImages) {
            if (imageObj.assetId === assetId) {
                return imageObj.sourceUrl;
            }
        }
        return '';
    }

    /**
     * Retrieves the Thuzz rating assets based on the provided value.
     *
     * @param value - The Thuzz rating value.
     * @returns An object containing the meaning and rating class for the Thuzz rating.
     */
    // eslint-disable-next-line class-methods-use-this
    public getThuzzRatingAssets(value: number): { meaning: string; ratingClass: string } {
        let meaning: string;
        let ratingClass: string;
        if (value >= 85) {
            meaning = Locale.getString(PreviewComponentAssets.StringAssets.THUZZ_RATING_GREAT);
            ratingClass = 'rating-1';
        } else if (value < 85 && value >= 65) {
            meaning = Locale.getString(PreviewComponentAssets.StringAssets.THUZZ_RATING_GOOD);
            ratingClass = 'rating-2';
        } else if (value < 65 && value >= 40) {
            meaning = Locale.getString(PreviewComponentAssets.StringAssets.THUZZ_RATING_OK);
            ratingClass = 'rating-3';
        } else {
            meaning = Locale.getString(PreviewComponentAssets.StringAssets.THUZZ_RATING_DULL);
            ratingClass = 'rating-4';
        }
        return { meaning, ratingClass };
    }

    /**
     * Retrieves the recording availability assets based on the provided recording availability.
     *
     * @param recordingAvailability - The recording availability status.
     * @returns An object containing the image asset URL and string asset for the recording
     * availability.
     */
    public getRecordingAvailabilityAssets(recordingAvailability: RecordingAvailability): {
        imageAssetUrl: string;
        stringAsset: string;
    } {
        let imageAssetUrl: string = '';
        let stringAsset: string = '';

        imageAssetUrl = this.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.NOT_RECORDABLE_CELL_ICON,
        );

        switch (recordingAvailability) {
            case RecordingAvailability.RecordingAndWatchNotAllowed:
            case RecordingAvailability.RecordingNotAllowed:
            case RecordingAvailability.RecordingNotAllowedForOffer:
            case RecordingAvailability.RecordingNotAllowedForNdvrDisabled:
                // TODO: This case is not used in the codebase, but it is kept for future use.
                break;
            case RecordingAvailability.RecordingNotAllowedForUpcellChannel:
                stringAsset = Locale.getString(
                    PreviewComponentAssets.StringAssets.STATUS_MSG_COMPLETE_COPYRIGHT_RESTRICTION,
                );
                break;
            case RecordingAvailability.RecordingNotAllowedForChannel:
                stringAsset = Locale.getString(
                    PreviewComponentAssets.StringAssets.STATUS_MSG_CHANNEL_REC_NOT_ALLOWED,
                );
                break;
            default:
                return { imageAssetUrl: '', stringAsset: '' };
        }
        return { imageAssetUrl, stringAsset };
    }

    /**
     * Retrieves the recording status assets based on the provided status and preview type.
     *
     * @param status - The recording status.
     * @param previewType - The type of the preview.
     * @returns An object containing the image asset URL and string asset for the recording status.
     */
    public getRecordingStatusAssets(
        status: string | undefined,
        previewType: string | undefined,
        isKeepForeverRecording: boolean | undefined,
    ): { imageAssetUrl: string; stringAsset: string } {
        let imageAssetUrl: string = '';
        let stringAsset: string = '';

        switch (status) {
            case RecordingStatus.CompleteRecording: {
                if (isKeepForeverRecording) {
                    stringAsset = Locale.getString(
                        PreviewComponentAssets.StringAssets.STATUS_MSG_COMPLETE_REC,
                    );
                    imageAssetUrl = this.getImageAssetUrlById(
                        PreviewComponentAssets.ImageAssets.KUID_ICON,
                    );
                }
                break;
            }
            case RecordingStatus.HasNoRecordingStatus:
                // TODO: This case is not used in the codebase, but it is kept for future use.
                break;

            case RecordingStatus.InProgressRecording:
                stringAsset = Locale.getString(
                    PreviewComponentAssets.StringAssets.STATUS_MSG_IN_PROGRESS_REC,
                );
                imageAssetUrl = this.getImageAssetUrlById(
                    PreviewComponentAssets.ImageAssets.RECORDING_NOW_ICON,
                );
                break;

            case RecordingStatus.RecoverableRecording:
                // TODO: This case is not used in the codebase, but it is kept for future use.
                break;

            case RecordingStatus.ScheduledAsOnePass:
                stringAsset = Locale.getString(
                    PreviewComponentAssets.StringAssets.STATUS_MSG_SCHEDULED_AS_ONE_PASS,
                );
                imageAssetUrl = this.getImageAssetUrlById(
                    PreviewComponentAssets.ImageAssets.ICON_STATUS_ONEPASS,
                );
                break;

            case RecordingStatus.ScheduledAsSingleExplicit:
                stringAsset = Locale.getString(
                    PreviewComponentAssets.StringAssets.STATUS_MSG_SCHEDULED_AS_SINGLE_EXPLICIT,
                );
                imageAssetUrl = this.getImageAssetUrlById(
                    PreviewComponentAssets.ImageAssets.SINGLE_EXPLICIT_RECORD_ICON,
                );
                break;

            default:
        }

        if (previewType) {
            stringAsset = stringAsset.replace(
                PreviewComponentAssets.Placeholders.PREVIEW_TYPE,
                this.getContentNameByPreviewType(previewType),
            );
        }

        return { imageAssetUrl, stringAsset };
    }

    /**
     * Retrieves the partial recording status assets based on the provided start time, end time, and
     * duration.
     *
     * @param startTime - The start time of the recording.
     * @param endTime - The end time of the recording.
     * @param duration - The duration of the recording.
     * @returns An object containing the image asset URL and string asset for the partial recording
     * status.
     */
    public getPartialRecordingStatusAssets(
        startTime: string,
        endTime: string,
        duration: number,
    ): { imageAssetUrl: string; stringAsset: string } {
        let stringAsset = Locale.getString(
            PreviewComponentAssets.StringAssets.STATUS_MSG_PARTIAL_REC,
        );
        stringAsset = stringAsset.replace(
            PreviewComponentAssets.Placeholders.START_TIME,
            new DateTime(startTime).to12HourClock(),
        );
        stringAsset = stringAsset.replace(
            PreviewComponentAssets.Placeholders.END_TIME,
            new DateTime(endTime).to12HourClock(),
        );
        stringAsset = stringAsset.replace(
            PreviewComponentAssets.Placeholders.DURATION,
            DateTime.secondsToString(duration),
        );

        const imageAssetUrl = this.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.KUID_PARTIAL_ICON,
        );
        return { imageAssetUrl, stringAsset };
    }

    /**
     * Retrieves the names of the star ratings based on the provided value.
     *
     * @param value - The value used to determine the star ratings.
     * @returns An array of strings representing the names of the star ratings.
     */
    // eslint-disable-next-line class-methods-use-this
    public getStarRatingNames(value): Array<string> {
        const retArr: Array<string> = ['', '', '', '', ''].fill('empty');

        let index = 0;
        let itrValue = value;
        while (itrValue > 0 && index < 5) {
            if (itrValue > 0 && itrValue < 1) {
                retArr[index] = 'half';
            } else {
                retArr[index] = 'full';
            }
            index += 1;
            itrValue -= 1;
        }
        return retArr;
    }

    /**
     * Retrieves the string asset based on the provided asset ID.
     *
     * @param assetId - The identifier of the string asset.
     * @returns A string representing the asset.
     */
    // eslint-disable-next-line class-methods-use-this
    public getStringAssestById(assetId: string): string {
        return Locale.getString(assetId) || '';
    }

    /**
     * Retrieves the assets for trickplay restrictions based on the provided restrictions and live
     * status.
     *
     * @param restrictions - An array of trickplay restrictions.
     * @param isLive - A boolean indicating if the content is live. Defaults to false.
     * @returns An object containing the image asset URL and string asset for the trickplay
     * restrictions.
     */
    public getTrickplayRestrictionAssets(
        restrictions: Array<TrickPlay>,
        isLive: boolean = false,
    ): { imageAssetUrl: string; stringAsset: string } {
        let stringAsset = Locale.getString(
            PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_IS_NOT_ALLOWED,
        );
        // <list of restricted TrickPlay modes> <is/are> not <supported/allowed>
        const imageAssetUrl = this.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.TRICKPLAY_RESTRICTED_ICON,
        );

        if (restrictions.length > 1) {
            stringAsset = Locale.getString(
                PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_ARE_NOT_ALLOWED,
            );
        }

        if (isLive) {
            stringAsset = Locale.getString(
                PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_IS_NOT_SUPPORTED,
            );
            if (restrictions.length > 1) {
                stringAsset = Locale.getString(
                    PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_ARE_NOT_SUPPORTED,
                );
            }
        }

        const displayRestrictions = restrictions.map((item) => {
            let restrictionAsset =
                PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_UNKNOWN;
            if (item === TrickPlay.FastForward) {
                restrictionAsset =
                    PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_FAST_FORWARD;
            } else if (item === TrickPlay.Pause) {
                restrictionAsset = PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_PAUSE;
            } else if (item === TrickPlay.Rewind) {
                restrictionAsset = PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_REWIND;
            }
            return Locale.getString(restrictionAsset);
        });
        let displayRestrictionsText = displayRestrictions[0];
        if (displayRestrictions.length > 1) {
            displayRestrictionsText = `${displayRestrictions.slice(0, -1).join(', ')} ${Locale.getString(
                PreviewComponentAssets.StringAssets.TRICKPLAY_RESTRICTION_AND_TEXT,
            )} ${displayRestrictions[displayRestrictions.length - 1]}`;
        }

        stringAsset = stringAsset.replace(
            PreviewComponentAssets.Placeholders.RESTRICTED_TRICKPLAY_MODES,
            displayRestrictionsText,
        );

        return {
            imageAssetUrl,
            stringAsset,
        };
    }

    /**
     * Retrieves the string representation for starting recording a specified number of minutes
     * early.
     *
     * @param minutes - The number of minutes to start recording early.
     * @returns A string representing the start recording early label.
     */
    // TODO: We do not need this method once we will have recording options preview CCU API
    // eslint-disable-next-line class-methods-use-this
    public getStartRecordingMinutesEarlyString(minutes: number): string {
        let stringAsset: string = Locale.getString(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_MINUTE_EARLY,
        );
        stringAsset = stringAsset.replace(
            PreviewComponentAssets.Placeholders.MINUTE,
            minutes.toString(),
        );
        if (minutes > 1) {
            stringAsset = Locale.getString(
                PreviewComponentAssets.StringAssets.PREVIEW_LABEL_MINUTES_EARLY,
            );
            stringAsset = stringAsset.replace(
                PreviewComponentAssets.Placeholders.MINUTES,
                minutes.toString(),
            );
        }
        return stringAsset;
    }

    /**
     * Retrieves the string representation for stopping recording a specified number of minutes
     * late.
     *
     * @param minutes - The number of minutes to stop recording late.
     * @returns A string representing the stop recording late label.
     */
    // TODO: We do not need this method once we will have recording options preview CCU API
    // eslint-disable-next-line class-methods-use-this
    public getStopRecordingMinutesLateString(minutes: number): string {
        let stringAsset = Locale.getString(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_MINUTE_LONGER,
        );
        stringAsset = stringAsset.replace(
            PreviewComponentAssets.Placeholders.MINUTE,
            minutes.toString(),
        );
        if (minutes > 1) {
            stringAsset = Locale.getString(
                PreviewComponentAssets.StringAssets.PREVIEW_LABEL_MINUTES_LONGER,
            );
            stringAsset = stringAsset.replace(
                PreviewComponentAssets.Placeholders.MINUTES,
                minutes.toString(),
            );
        }
        if (minutes === 60) {
            stringAsset = Locale.getString(
                PreviewComponentAssets.StringAssets.PREVIEW_LABEL_HOUR_LONGER,
            );
            stringAsset = stringAsset.replace(PreviewComponentAssets.Placeholders.HOUR, '1');
        }
        if (minutes === 90) {
            stringAsset = Locale.getString(
                PreviewComponentAssets.StringAssets.PREVIEW_LABEL_HOURS_LONGER,
            );
            stringAsset = stringAsset.replace(PreviewComponentAssets.Placeholders.HOURS, '1 1/2');
        }
        if (minutes === 180) {
            stringAsset = Locale.getString(
                PreviewComponentAssets.StringAssets.PREVIEW_LABEL_HOURS_LONGER,
            );
            stringAsset = stringAsset.replace(PreviewComponentAssets.Placeholders.HOURS, '3');
        }
        return stringAsset;
    }
}
